import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import './app.css';

import { ErrorBoundary, Component as Root } from '~/routes/layouts/root';
import { Component as NewPage } from '~/routes/new';
import { Component as DynamicPage } from '~/routes/dynamic-route';
import { Component as QuotePage } from '~/routes/quote';
import { Component as BasketPage } from '~/routes/basket';

Sentry.init({
  environment: import.meta.env.VITE_ENVIRONMENT || 'Debug',
  release: import.meta.env.VITE_VERSION || '0.0.0',
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      networkDetailAllowUrls: [window.location.origin],
      networkResponseHeaders: [
        'x-tenant',
        'x-region',
        'x-trace',
        'x-cache',
        'cf-cache-status',
        'x-amzn-trace-id',
        'cache-control'
      ],
      unblock: ['.sentry-unblock, [data-sentry-unblock]'],
      unmask: ['.sentry-unmask, [data-sentry-unmask]']
    }),
    Sentry.thirdPartyErrorFilterIntegration({
      filterKeys: ['parcel2go-uiz'],
      behaviour: 'drop-error-if-contains-third-party-frames'
    }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ],
  beforeSend(event, hint) {
    // Filter out CustomEvent unhandledrejection noise
    const message = event.message || hint.originalException?.toString() || '';
    const exceptionMessage = event.exception?.values?.[0]?.value || '';
    const combinedMessage = `${message} ${exceptionMessage}`;

    if (
      combinedMessage.includes('CustomEvent') &&
      combinedMessage.includes('unhandledrejection') &&
      combinedMessage.includes('captured as promise rejection')
    ) {
      return null; // Don't send this event to Sentry
    }
    return event;
  },
  // Performance Monitoring
  tracesSampleRate: 0.1, //  Capture 10% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    path: ':prefix?',
    element: <Root />,
    children: [
      {
        path: ':prefix/quote',
        element: <QuotePage />,
        errorElement: <ErrorBoundary />
      },
      {
        path: ':prefix/confirm',
        element: <BasketPage />,
        errorElement: <ErrorBoundary />
      },
      {
        path: ':prefix/complete',
        lazy: () => import('~/routes/complete'),
        errorElement: <ErrorBoundary />
      },
      {
        path: ':prefix/buy-again',
        lazy: () => import('~/routes/buy-again'),
        errorElement: <ErrorBoundary />
      },
      {
        path: ':prefix/debug',
        lazy: () => import('~/routes/debug'),
        errorElement: <ErrorBoundary />
      },
      {
        path: ':prefix/payment',
        lazy: () => import('~/routes/payment'),
        errorElement: <ErrorBoundary />
      },
      {
        path: ':prefix/new',
        element: <NewPage />,
        errorElement: <ErrorBoundary />
      },
      {
        path: ':prefix/legacy',
        element: <NewPage />,
        errorElement: <ErrorBoundary />
      },
      {
        path: ':prefix/:screen',
        element: <DynamicPage />,
        errorElement: <ErrorBoundary />
      }
    ]
  }
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <RouterProvider router={router} />
    </Sentry.ErrorBoundary>
  </StrictMode>
);
